// Impressao e download em PDF de etiquetas de QR Code (uma ou varias).
//
// Por que a impressao NAO usa window.print() na propria pagina: o modal e um
// portal fixed (position:fixed) por cima do app. Impressao de elementos
// position:fixed e historicamente inconsistente entre navegadores - varios
// simplesmente NAO renderizam elemento fixed na folha impressa, entao a
// "etiqueta" some e sobra so o app por tras (o bug relatado: "so aparece o
// site da Lizzi e nada de QR Code"). A tecnica anterior tambem dependia do
// seletor CSS :has(), sem suporte em navegadores mais antigos.
//
// Aqui a impressao abre uma JANELA A PARTE com um documento minimo (sem app,
// sem position:fixed) contendo so as etiquetas como <img> (PNG dos canvases
// ja renderizados) - isso imprime igual em qualquer navegador.

import { jsPDF } from 'jspdf';

export interface EtiquetaQrParaImprimir {
  titulo: string;
  subtitulo?: string;
  dataUrl: string;
}

const DIMENSOES_MM: Record<'sm' | 'md' | 'lg', { w: number; h: number }> = {
  sm: { w: 50, h: 50 },
  md: { w: 72, h: 95 },
  lg: { w: 90, h: 120 },
};

function escaparHtml(texto: string): string {
  const div = document.createElement('div');
  div.textContent = texto;
  return div.innerHTML;
}

export function imprimirEtiquetasQr(itens: EtiquetaQrParaImprimir[], tamanho: 'sm' | 'md' | 'lg'): void {
  if (!itens.length) return;
  const { w, h } = DIMENSOES_MM[tamanho];

  const janela = window.open('', '_blank', 'width=900,height=700');
  if (!janela) {
    window.alert('Não foi possível abrir a janela de impressão. Verifique se o navegador está bloqueando pop-ups para este site.');
    return;
  }

  const etiquetasHtml = itens
    .map(
      (item) => `
        <div class="etq">
          <div class="etq-titulo">${escaparHtml(item.titulo)}</div>
          ${item.subtitulo ? `<div class="etq-subtitulo">${escaparHtml(item.subtitulo)}</div>` : ''}
          <img class="etq-qr" src="${item.dataUrl}" alt="QR Code" />
          <div class="etq-marca">Manux Service</div>
        </div>
      `,
    )
    .join('');

  janela.document.open();
  janela.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>Etiquetas QR Code</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
  .etq {
    width: ${w}mm; min-height: ${h}mm; margin: 0 auto; padding: 8mm 6mm 7mm;
    display: flex; flex-direction: column; align-items: center; justify-content: flex-start; text-align: center;
    page-break-after: always; break-after: page;
  }
  .etq:last-child { page-break-after: auto; break-after: auto; }
  .etq-titulo { font-size: 11pt; font-weight: 700; color: #0f172a; }
  .etq-subtitulo { font-size: 8.5pt; color: #475569; margin-top: 2px; }
  .etq-qr { margin-top: 10px; width: 60%; max-width: ${Math.min(w, h) - 16}mm; height: auto; }
  .etq-marca { margin-top: 10px; font-size: 7.5pt; letter-spacing: .14em; color: #7b88a1; text-transform: uppercase; }
  @page { margin: 8mm; }
</style>
</head>
<body>${etiquetasHtml}</body>
</html>`);
  janela.document.close();

  // As imagens sao data URLs (ja "carregadas" de imediato, sem rede), mas um
  // pequeno atraso garante que o layout terminou antes do print() abrir.
  setTimeout(() => {
    janela.focus();
    janela.print();
  }, 250);
}

// Pequeno icone de tesoura vetorial (X + dois "aneis"), sem depender de fonte/glifo.
function desenharTesoura(doc: jsPDF, cx: number, cy: number, tamanho: number) {
  const s = tamanho;
  doc.setDrawColor(120);
  doc.setLineWidth(0.2);
  doc.line(cx - s / 2, cy - s / 2, cx + s / 2, cy + s / 2);
  doc.line(cx - s / 2, cy + s / 2, cx + s / 2, cy - s / 2);
  doc.circle(cx - s / 2, cy - s / 2, s / 6);
  doc.circle(cx - s / 2, cy + s / 2, s / 6);
}

function esperarFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

// Gera um PDF A4 com uma ou varias etiquetas (grade + marcas de corte quando
// couber mais de uma por folha) e dispara o download. A cada 8 etiquetas
// solta o thread principal - sem isso, um lote grande (200+ equipamentos)
// trava a aba inteira ate' o fim do loop, e ate' o "Cancelar" fica sem
// resposta enquanto isso roda.
export async function baixarPdfEtiquetasQr(
  itens: EtiquetaQrParaImprimir[],
  tamanho: 'sm' | 'md' | 'lg',
  aoProgredir?: (feitas: number) => void,
): Promise<void> {
  if (!itens.length) return;
  const { w: labelW, h: labelH } = DIMENSOES_MM[tamanho];

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210;
  const pageH = 297;
  const margin = 8;
  const gutter = 5;
  const usableW = pageW - margin * 2;
  const usableH = pageH - margin * 2;
  const cols = Math.max(1, Math.floor((usableW + gutter) / (labelW + gutter)));
  const rows = Math.max(1, Math.floor((usableH + gutter) / (labelH + gutter)));
  const porPagina = cols * rows;

  for (let idx = 0; idx < itens.length; idx++) {
    const item = itens[idx];
    if (idx > 0 && idx % 8 === 0) {
      aoProgredir?.(idx);
      await esperarFrame();
    }
    const posNaPagina = idx % porPagina;
    if (idx > 0 && posNaPagina === 0) doc.addPage();
    const col = posNaPagina % cols;
    const lin = Math.floor(posNaPagina / cols);
    const x = margin + col * (labelW + gutter);
    const y = margin + lin * (labelH + gutter);

    // Linha de corte tracejada ao redor da etiqueta.
    doc.setDrawColor(160);
    doc.setLineWidth(0.15);
    doc.setLineDashPattern([1, 1], 0);
    doc.rect(x, y, labelW, labelH);
    doc.setLineDashPattern([], 0);

    // Marcas de corte (crop marks) nos 4 cantos, no espaço entre etiquetas.
    const marca = 3;
    const vao = 1;
    doc.setDrawColor(110);
    doc.setLineWidth(0.2);
    [
      [x, y, -1, -1],
      [x + labelW, y, 1, -1],
      [x, y + labelH, -1, 1],
      [x + labelW, y + labelH, 1, 1],
    ].forEach(([px, py, dx, dy]) => {
      doc.line(px, py + dy * vao, px, py + dy * (vao + marca));
      doc.line(px + dx * vao, py, px + dx * (vao + marca), py);
    });

    // Icone de tesoura + texto indicando o corte, centralizado no topo da etiqueta.
    desenharTesoura(doc, x + labelW / 2 - 7, y - 2.6, 3);
    doc.setFontSize(6.5);
    doc.setTextColor(120);
    doc.text('corte aqui', x + labelW / 2 + 1, y - 1.3, { align: 'center' });

    const innerPad = 3;
    let cursorY = y + innerPad + 3;
    doc.setFontSize(8);
    doc.setTextColor(20);
    const tituloLinhas = doc.splitTextToSize(item.titulo, labelW - innerPad * 2);
    doc.text(tituloLinhas, x + labelW / 2, cursorY, { align: 'center' });
    cursorY += tituloLinhas.length * 3.2;

    if (item.subtitulo) {
      doc.setFontSize(6.5);
      doc.setTextColor(90);
      const subLinhas = doc.splitTextToSize(item.subtitulo, labelW - innerPad * 2);
      doc.text(subLinhas, x + labelW / 2, cursorY, { align: 'center' });
      cursorY += subLinhas.length * 2.6;
    }

    const espacoRestante = y + labelH - innerPad - cursorY;
    const qrFinal = Math.max(15, Math.min(labelW - innerPad * 2, espacoRestante));
    doc.addImage(item.dataUrl, 'PNG', x + (labelW - qrFinal) / 2, cursorY, qrFinal, qrFinal);
  }

  doc.save(itens.length === 1 ? 'qr-code-equipamento.pdf' : 'qr-codes-equipamentos.pdf');
}
